import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { formatIsoDateShort } from "@/lib/bookingDates";
import {
  fetchMoveOptions,
  relocateStay,
  type MoveOptions,
  type MovePitchOption,
} from "@/lib/pitchAssignmentOptions";
import type { Booking, Pitch } from "@/types/camping";

export interface StayRelocationPanelProps {
  booking: Booking;
  pitches: Pitch[];
  canEdit?: boolean;
  draftArrival?: string;
  draftDeparture?: string;
  onUpdated: (booking: Booking) => void;
}

function pitchCode(pitches: Pitch[], pitchId: string | null): string {
  if (!pitchId) return "ohne Platz";
  const pitch = pitches.find((item) => item.id === pitchId);
  if (!pitch) return "Platz";
  return pitch.label ? `${pitch.code} · ${pitch.label}` : pitch.code;
}

export function StayRelocationPanel({
  booking,
  pitches,
  canEdit = false,
  draftArrival,
  draftDeparture,
  onUpdated,
}: StayRelocationPanelProps) {
  const arrival = draftArrival || booking.arrival_date;
  const departure = draftDeparture || booking.departure_date;
  const extending = departure > booking.departure_date;
  const segments = booking.pitch_segments ?? [];
  const [moveDate, setMoveDate] = useState<string | null>(null);
  const [options, setOptions] = useState<MoveOptions | null>(null);
  const [pitchId, setPitchId] = useState("");
  const [reason, setReason] = useState("");
  const [ignoreRules, setIgnoreRules] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMoveDate(null);
    setPitchId("");
    setOptions(null);
    setError(null);
  }, [booking.id, booking.pitch_id, booking.departure_date]);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    setLoading(true);
    const moveInStay = Boolean(moveDate && moveDate >= arrival && moveDate < departure);
    const requestedMove = moveInStay ? moveDate ?? undefined : undefined;
    void fetchMoveOptions(booking.id, {
      departureDate: departure,
      moveDate: requestedMove,
    })
      .then((response) => {
        if (cancelled) return;
        setOptions(response);
        setError(null);
        if (!moveInStay) setMoveDate(response.move_date);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Plätze konnten nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [arrival, booking.id, booking.version, canEdit, departure, moveDate]);

  const choices = (options?.pitches ?? []).filter((option) => {
    if (option.is_current && options?.current_pitch_free) return false;
    if (option.available) return true;
    if (ignoreRules && option.rule_warning && !option.unavailable_reason) return true;
    return showBlocked;
  });
  const sameCategory = choices.filter((option) => option.same_category && canSelect(option, ignoreRules));
  const otherCategory = choices.filter((option) => !option.same_category && canSelect(option, ignoreRules));
  const blocked = showBlocked
    ? choices.filter((option) => !canSelect(option, ignoreRules))
    : [];

  async function submit() {
    if (!moveDate || !pitchId) {
      setError("Bitte Umzugstag und Zielplatz wählen.");
      return;
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Bitte geben Sie eine Begründung an.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await relocateStay(booking.id, {
        version: booking.version ?? 1,
        move_date: moveDate,
        pitch_id: pitchId,
        departure_date: departure !== booking.departure_date ? departure : undefined,
        reason: trimmed,
        ignore_rules: ignoreRules,
      });
      setReason("");
      setPitchId("");
      onUpdated(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Umzug fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-separator pt-3">
      <h4 className="text-sm font-semibold text-label">Umziehen</h4>
      {segments.length > 1 ? (
        <ul className="space-y-1 text-sm text-label">
          {segments.map((segment) => (
            <li key={`${segment.from_date}-${segment.pitch_id ?? "none"}`}>
              {formatIsoDateShort(segment.from_date)} – {formatIsoDateShort(segment.to_date)}:{" "}
              {pitchCode(pitches, segment.pitch_id)}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-xs text-label-secondary">
        Ab dem Umzugstag gilt der neue Platz, auch wenn er in einer anderen Kategorie liegt. Bis
        dahin bleibt der bisherige Platz belegt. Der Preis wird neu berechnet; schon erstellte
        Rechnungen bleiben unverändert.
      </p>
      {extending && options?.current_pitch_free ? (
        <p className="text-sm text-status-green">
          Die Verlängerung bis {formatIsoDateShort(departure)} ist auf dem aktuellen Platz möglich.
          Speichern Sie die Buchung, damit die neue Abreise gilt.
        </p>
      ) : null}
      {extending && options && !options.current_pitch_free ? (
        <p className="text-sm text-status-red">
          Auf dem aktuellen Platz geht die Verlängerung nicht. Der Gast kann ab{" "}
          {formatIsoDateShort(options.suggested_move_date)} auf einen der freien Plätze umziehen.
        </p>
      ) : null}
      {canEdit ? (
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Umzugstag</span>
            <input
              type="date"
              className="eh-input"
              value={moveDate ?? ""}
              min={arrival}
              max={shiftDate(departure, -1)}
              disabled={saving}
              onChange={(event) => setMoveDate(event.target.value || null)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Begründung *</span>
            <textarea
              className="eh-input min-h-[4rem] resize-y"
              value={reason}
              rows={2}
              disabled={saving}
              placeholder="z. B. Gast möchte verlängern, Nachbarplatz, Kategoriewechsel …"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-label">
            <input
              type="checkbox"
              className="mt-1"
              checked={ignoreRules}
              disabled={saving}
              onChange={(event) => setIgnoreRules(event.target.checked)}
            />
            <span>Länge, Strom und Vertragspflicht ignorieren, wenn der Gast den Platz trotzdem nehmen soll.</span>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Zielplatz</span>
            <select
              className="eh-select-input"
              value={pitchId}
              disabled={saving || loading}
              onChange={(event) => setPitchId(event.target.value)}
            >
              <option value="">{loading ? "Plätze werden geladen …" : "— Platz wählen —"}</option>
              {sameCategory.length > 0 ? (
                <optgroup label="Gleiche Kategorie">
                  {sameCategory.map((option) => (
                    <option key={option.pitch_id} value={option.pitch_id}>
                      {optionLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {otherCategory.length > 0 ? (
                <optgroup label="Andere Kategorie">
                  {otherCategory.map((option) => (
                    <option key={option.pitch_id} value={option.pitch_id}>
                      {optionLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {blocked.length > 0 ? (
                <optgroup label="Nicht frei">
                  {blocked.map((option) => (
                    <option key={option.pitch_id} value={option.pitch_id} disabled>
                      {optionLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" loading={saving} disabled={loading} onClick={() => void submit()}>
              Umziehen
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={() => setShowBlocked((value) => !value)}
            >
              {showBlocked ? "Belegte Plätze ausblenden" : "Auch belegte Plätze zeigen"}
            </Button>
          </div>
          {selectedWarning(options, pitchId, ignoreRules) ? (
            <p className="text-xs text-label-secondary">{selectedWarning(options, pitchId, ignoreRules)}</p>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </div>
  );
}

function canSelect(option: MovePitchOption, ignoreRules: boolean): boolean {
  if (option.unavailable_reason) return false;
  if (option.available) return true;
  return ignoreRules && Boolean(option.rule_warning);
}

function optionLabel(option: MovePitchOption): string {
  const name = option.label ? `${option.code} · ${option.label}` : option.code;
  const category = option.category_name ? ` (${option.category_name})` : "";
  const hint = option.unavailable_reason || option.rule_warning;
  return hint ? `${name}${category} – ${hint}` : `${name}${category}`;
}

function selectedWarning(
  options: MoveOptions | null,
  pitchId: string,
  ignoreRules: boolean,
): string | null {
  const option = options?.pitches.find((item) => item.pitch_id === pitchId);
  if (!option || !option.rule_warning || option.unavailable_reason) return null;
  if (!ignoreRules) return `${option.rule_warning} Aktivieren Sie die Ausnahme, um den Platz trotzdem zu nehmen.`;
  return option.rule_warning;
}

function shiftDate(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  const value = new Date(Date.UTC(year, month - 1, day));
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}
